/**
 * Privacy utility functions for masking sensitive user data
 */

/**
 * Masks a user's name based on their privacy preferences
 * @param firstName - User's first name
 * @param lastName - User's last name
 * @param showFullName - Privacy preference flag
 * @param userId - User ID for generating consistent masked names
 * @returns Masked or full name based on privacy preference
 */
export function getDisplayName(
  firstName: string = 'User',
  lastName: string = '',
  showFullName: boolean = true,
  userId?: string
): string {
  // If showFullName is true, return full name
  if (showFullName) {
    // Handle email-based lastName (mask email addresses)
    if (lastName && lastName.includes('@')) {
      return firstName;
    }
    return lastName ? `${firstName} ${lastName}`.trim() : firstName;
  }

  // If showFullName is false, mask the name
  // Show first name with masked last name
  const maskedLastName = lastName && !lastName.includes('@') 
    ? lastName.charAt(0) + '***' 
    : '';
  
  return maskedLastName ? `${firstName} ${maskedLastName}` : firstName;
}

/**
 * Masks phone number based on privacy preferences
 * @param phoneNumber - Full phone number
 * @param showPhoneNumber - Privacy preference flag
 * @returns Masked or full phone number
 */
export function getDisplayPhone(
  phoneNumber: string,
  showPhoneNumber: boolean = false
): string {
  if (showPhoneNumber || !phoneNumber) {
    return phoneNumber;
  }

  // Mask middle digits: +91 98765-XX-XXX
  const cleaned = phoneNumber.replace(/\D/g, '');
  if (cleaned.length >= 10) {
    const lastDigits = cleaned.slice(-10);
    const visible = lastDigits.slice(0, 5);
    return `${visible}XXXXX`;
  }

  return 'XXXXXXXXXX';
}

/**
 * Checks if a user's profile should be visible based on privacy settings
 * @param profileVisibility - Privacy preference flag
 * @returns Boolean indicating if profile should be shown
 */
export function shouldShowProfile(profileVisibility: boolean = true): boolean {
  return profileVisibility;
}
